'use client';
import { Builder, builder, withChildren } from '@builder.io/react';
import { ConversionSchemaType } from '@sly/frontend/common/helpers/transformGwizzyCommunity';
import Footer from '@sly/frontend/react/shared/components/Footer';
import { Button, Heading, Link } from '@sly/frontend/react/system';
import { getIcon } from '@sly/icons/dynamic';
import { IconSize, IconSizes } from '@sly/icons/hooks/useIcon';
import { IconName, IconNames } from '@sly/icons/icon.types';

import CareScoutBanner from './CareScoutBanner';
import { builderApiKey } from './config';
import HomePageHeader from './homePage/components/HomePageHeader';
import PersonalizedSearch from './homePage/components/PersonalizedSearch';
import SeniorlyAgents from './homePage/components/SeniorlyAgents';
import SeniorlyFAQs, { FAQContent } from './homePage/components/SeniorlyFAQs';
import SeniorlyReasons from './homePage/components/SeniorlyReasons';
import SeniorlyReviews from './homePage/components/SeniorlyReviews';
import ShowcaseCarousel from './homePage/components/ShowcaseCarousel';
import VirtualAdvisorSection from './homePage/components/VirtualAdvisorSection';
import ExploreByCommunity from './hubPage/components/HubDynamicContent/ExploreByEntity';
import {
  ContactUsFormWrapper,
  ContactUsModal,
} from './marketingPages/components/ContactUsForm';
import { ExpertHelpCtaWrapper } from '../../../packages/frontend/src/react/cta/ExpertHelpCta';
import GwizzyCta from '../../../packages/frontend/src/react/cta/GwizzyCta';
import InlineGwizzyAccordian from '../../../packages/frontend/src/react/gwizzy/components/InlineGwizzyAccordian';
import Accordian from '../../../packages/frontend/src/react/profile/v2/Accordian';
import SearchBoxContainer from '../../../packages/frontend/src/react/search/components/SeachBoxContainer';
import HeaderContainer, {
  HeaderLowerMenuContainer,
} from '../../../packages/frontend/src/react/shared/components/Header/Header.container';
builder.init(builderApiKey);

Builder.registerComponent(
  ({ iconName, size }: { iconName: IconName; size: IconSize }) => {
    const Icon = getIcon(iconName || 'Care');
    return <Icon size={size} />;
  },
  {
    name: 'Icon',
    inputs: [
      {
        name: 'iconName',
        type: 'string',
        required: true,
        enum: IconNames,
      },
      {
        name: 'size',
        type: 'string',
        enum: IconSizes,
        defaultValue: 'm',
      },
    ],
  }
);

Builder.registerComponent(CareScoutBanner, {
  name: 'CareScoutBanner',
  inputs: [],
});

Builder.registerComponent(Button, {
  name: 'Button',
  inputs: [
    {
      name: 'children',
      type: 'string',
      hideFromUI: true,
      meta: {
        ts: 'ReactNode',
      },
    },
    {
      name: 'variant',
      type: 'string',
      enum: ['primary', 'secondary', 'tertiary', 'text', 'link'],
      defaultValue: 'primary',
    },
    {
      name: 'palette',
      type: 'string',
      enum: [
        'viridian',
        'white',
        'black',
        'gray',
        'red',
        'orange',
        'yellow',
        'green',
        'blue',
        'purple',
      ],
      defaultValue: 'viridian',
    },
    {
      name: 'size',
      type: 'string',
      enum: ['small', 'medium', 'large'],
      defaultValue: 'medium',
    },
    {
      name: 'disabled',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'loading',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'fullWidth',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'href',
      type: 'string',
    },
    {
      name: 'target',
      type: 'string',
      enum: ['_self', '_blank'],
      defaultValue: '_self',
    },
    {
      name: 'className',
      type: 'string',
    },
    {
      name: 'eventProps',
      type: 'string',
      meta: {
        ts: 'Record<string, string>',
      },
      required: true,
    },
  ],
});

Builder.registerComponent(
  ({ text }: { text: string }) => {
    return (
      <div
        className='dynamic-editor'
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  },
  {
    name: 'Text',
    static: true,
    override: true,
    inputs: [
      {
        name: 'text',
        type: 'html',
        required: true,
        autoFocus: true,
        bubble: true,
        defaultValue: 'Enter some text...',
      },
    ],
    defaultStyles: {
      lineHeight: 'normal',
      height: 'auto',
      textAlign: 'center',
    },
  }
);

Builder.registerComponent(Link, {
  name: 'Link',
  inputs: [
    {
      name: 'children',
      type: 'string',
      hideFromUI: true,
      meta: {
        ts: 'ReactNode',
      },
    },
    {
      name: 'href',
      type: 'string',
    },
    {
      name: 'target',
      type: 'string',
      enum: ['_self', '_blank'],
      defaultValue: '_self',
    },
    {
      name: 'className',
      type: 'string',
    },
    {
      name: 'eventProps',
      type: 'string',
      meta: {
        ts: 'Record<string, string>',
      },
      required: true,
    },
  ],
});

Builder.registerComponent(HeaderLowerMenuContainer, {
  name: 'HeaderLowerMenu',
  inputs: [],
});

Builder.registerComponent(HeaderContainer, {
  name: 'HeaderContainer',
  inputs: [
    {
      name: 'showSearch',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'showLowerMenu',
      type: 'boolean',
      defaultValue: true,
    },
  ],
});

Builder.registerComponent(Heading, {
  name: 'Heading',
  inputs: [
    {
      name: 'text',
      type: 'string',
      meta: {
        ts: 'ReactNode',
      },
    },
    {
      name: 'font',
      type: 'string',
      enum: [
        'font-t-l',
        'font-t-xl',
        'font-b-m',
        'font-t-m',
        'font-t-s-azo',
        'font-t-xxl',
        'font-b-l',
        'font-t-m-azo',
        'font-t-s',
        'font-t-xs-azo',
        'font-t-l md:font-t-xl',
        'font-b-xs',
        'font-l-m',
        'font-b-s',
      ],
      defaultValue: 'title-xxlarge',
    },
    {
      name: 'as',
      type: 'enum',
      enum: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      defaultValue: 'h2',
    },
  ],
});

Builder.registerComponent(withChildren(Accordian), {
  name: 'Accordian',
  inputs: [
    {
      name: 'children',
      type: 'string',
      hideFromUI: true,
      meta: {
        ts: 'ReactElement<any, any>',
      },
    },
    {
      name: 'className',
      type: 'string',
    },
    {
      name: 'eventProps',
      type: 'string',
      meta: {
        ts: 'Record<string, string>',
      },
      required: true,
    },
    {
      name: 'openByDefault',
      type: 'boolean',
      required: true,
    },
    {
      name: 'renderOnlyIfOpen',
      type: 'boolean',
    },
    {
      name: 'title',
      type: 'string',
      meta: {
        ts: 'any',
      },
      required: true,
    },
    {
      name: 'variant',
      type: 'enum',
      enum: ['A', 'B'],
      required: true,
    },
    {
      name: 'wrapperClassName',
      type: 'string',
    },
  ],
});

Builder.registerComponent(GwizzyCta, {
  name: 'GwizzyCta',
  inputs: [
    {
      name: 'ctaProps',
      type: 'object',
      subFields: [
        {
          name: 'variant',
          type: 'string',
          enum: ['primary', 'secondary', 'tertiary', 'text', 'link'],
          required: true,
          defaultValue: 'primary',
        },
        {
          name: 'palette',
          type: 'string',
          enum: [
            'viridian',
            'white',
            'black',
            'gray',
            'red',
            'orange',
            'yellow',
            'green',
            'blue',
            'purple',
          ],
          required: true,
          defaultValue: 'viridian',
        },
        {
          name: 'text',
          type: 'string',
          required: true,
        },
      ],
    },
    {
      name: 'buttonStyle',
      type: 'object',
      meta: {
        ts: 'Record<string, string>',
      },
      defaultValue: {},
    },
    {
      name: 'eventProps',
      type: 'object',
      meta: {
        ts: 'Record<string, string>',
      },
    },
    {
      name: 'className',
      type: 'string',
    },
    {
      name: 'mode',
      type: 'string',
    },
    {
      name: 'isOpenByDefault',
      type: 'boolean',
    },
    {
      name: 'hideClose',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'params',
      type: 'object',
    },
    {
      name: 'showLocationStep',
      type: 'boolean',
    },
    {
      name: 'gwizzyCommunity',
      type: 'object',
    },
    {
      name: 'submitText',
      type: 'string',
    },
    {
      name: 'conversionSchemaOverride',
      type: 'enum',
      enum: [
        ConversionSchemaType.PaidAd,
        ConversionSchemaType.D2C,
        ConversionSchemaType.NoAgent,
        ConversionSchemaType.ActiveAdult,
        ConversionSchemaType.Gwizzy,
        ConversionSchemaType.homeCareAdTile,
      ],
      defaultValue: ConversionSchemaType.PaidAd,
    },
    {
      name: 'isFullScreen',
      type: 'boolean',
      defaultValue: false,
    },
  ],
});

Builder.registerComponent(InlineGwizzyAccordian, {
  name: 'InlineGwizzy',
  inputs: [
    {
      name: 'showLocationStep',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'submitText',
      type: 'string',
      defaultValue: '',
    },
    {
      name: 'skipHomebaseRedirect',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'conversionSchemaOverride',
      type: 'enum',
      enum: [
        ConversionSchemaType.PaidAd,
        ConversionSchemaType.D2C,
        ConversionSchemaType.NoAgent,
        ConversionSchemaType.ActiveAdult,
        ConversionSchemaType.Gwizzy,
        ConversionSchemaType.homeCareAdTile,
      ],
      defaultValue: ConversionSchemaType.Gwizzy,
    },
  ],
});

Builder.registerComponent(ExploreByCommunity, {
  name: 'ExploreByCommunity',
  inputs: [
    {
      name: 'entity',
      type: 'string',
      enum: ['agents', 'communities'],
      required: true,
    },
    {
      name: 'tabList',
      type: 'list',
      subFields: [
        {
          name: 'title',
          type: 'string',
        },
        {
          name: 'slugs',
          type: 'string',
        },
      ],
      meta: {
        ts: 'TabListItem[]',
      },
      required: true,
    },
    {
      name: 'serverRender',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'title',
      type: 'string',
      required: true,
    },
  ],
});

// Builder.registerComponent(ReviewsSectionWrapper, {
//   name: 'ReviewsSectionWrapper',
//   inputs: [
//     {
//       name: 'slugs',
//       type: 'string',
//       required: true,
//     },
//     {
//       name: 'title',
//       type: 'string',
//       required: true,
//     },
//   ],
// });

Builder.registerComponent(Footer, {
  name: 'Footer',
  inputs: [
    {
      name: 'hideLinks',
      type: 'boolean',
    },
  ],
});

Builder.registerComponent(SeniorlyReviews, {
  name: 'SeniorlyReviews',
  inputs: [
    {
      name: 'quotes',
      type: 'list',
      meta: {
        ts: 'Quote[]',
      },
      subFields: [
        {
          name: 'quote',
          type: 'string',
          required: true,
        },
        {
          name: 'author',
          type: 'string',
          required: true,
        },
        {
          name: 'relation',
          type: 'string',
          required: true,
        },
        {
          name: 'avatar',
          type: 'object',
          subFields: [
            {
              name: 'path',
              type: 'string',
            },
          ],
          required: true,
        },
        {
          name: 'stars',
          type: 'number',
        },
        {
          name: 'date',
          type: 'string',
        },
      ],
      required: true,
    },
    {
      name: 'title',
      type: 'string',
    },
  ],
});

Builder.registerComponent(SeniorlyAgents, {
  name: 'SeniorlyAgents',
  inputs: [
    {
      name: 'hideJoinButton',
      type: 'boolean',
    },
  ],
});

Builder.registerComponent(VirtualAdvisorSection, {
  name: 'VirtualAdvisorSection',
});

Builder.registerComponent(SeniorlyReasons, {
  name: 'SeniorlyReasons',
});

Builder.registerComponent(SeniorlyFAQs, {
  name: 'SeniorlyFAQs',
  inputs: [
    {
      name: 'FAQs',
      type: 'list',
      subFields: [
        {
          name: 'title',
          type: 'string',
        },
        {
          name: 'description',
          type: 'string',
        },
      ],
      meta: {
        ts: '{ title: string; description: string; }[]',
      },
      required: true,
    },
  ],
});

Builder.registerComponent(FAQContent, {
  name: 'FAQs',
  inputs: [
    {
      name: 'faqs',
      type: 'list',
      subFields: [
        {
          name: 'question',
          type: 'string',
        },
        {
          name: 'answer',
          type: 'string',
        },
      ],
    },
  ],
});

Builder.registerComponent(PersonalizedSearch, {
  name: 'PersonalizedSearch',
});

Builder.registerComponent(HomePageHeader, {
  name: 'HomePageHeader',
});

Builder.registerComponent(ContactUsFormWrapper, {
  name: 'ContactUsFormWrapper',
  inputs: [
    {
      name: 'submitButtonText',
      type: 'string',
    },
    {
      name: 'showLocation',
      type: 'boolean',
    },
    {
      name: 'showJobEnquiry',
      type: 'boolean',
    },
  ],
});

Builder.registerComponent(ContactUsModal, {
  name: 'ContactUsModal',
  inputs: [
    {
      name: 'submitButtonText',
      type: 'string',
    },
    {
      name: 'headingText',
      type: 'string',
    },
    {
      name: 'showLocation',
      type: 'boolean',
    },
    {
      name: 'buttonText',
      type: 'string',
      required: true,
    },
    {
      name: 'showJobEnquiry',
      type: 'boolean',
    },
  ],
});

Builder.registerComponent(withChildren(ShowcaseCarousel), {
  name: 'ShowcaseCarousel',
  canHaveChildren: true,
  inputs: [
    {
      name: 'header',
      type: 'string',
    },
    {
      name: 'description',
      type: 'string',
    },
    {
      name: 'buttonText',
      type: 'string',
    },
    {
      name: 'buttonLink',
      type: 'string',
    },
    {
      name: 'controlPosition',
      type: 'string',
      enum: ['sides', 'top'],
      defaultValue: 'sides',
    },
    {
      name: 'showKeySection',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'showCompassSection',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'className',
      type: 'string',
    },
    {
      name: 'eventProps',
      type: 'object',
      meta: {
        ts: 'Record<string, string>',
      },
    },
  ],
});

Builder.registerComponent(SearchBoxContainer, {
  name: 'SearchBoxContainer',
  inputs: [
    {
      name: 'placeholder',
      type: 'string',
      defaultValue: 'Search by location or community name',
    },
    {
      name: 'showSearchIcon',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'disableGoogleSearch',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'className',
      type: 'string',
    },
    {
      name: 'eventProps',
      type: 'object',
      meta: {
        ts: 'Record<string, string>',
      },
    },
  ],
});

Builder.registerComponent(ExpertHelpCtaWrapper, {
  name: 'ExpertHelpCta',
  inputs: [
    {
      name: 'ctaProps',
      type: 'object',
      subFields: [
        {
          name: 'text',
          type: 'string',
        },
      ],
    },
    {
      name: 'agentSlug',
      type: 'string',
    },
    {
      name: 'eventProps',
      type: 'object',
      meta: {
        ts: 'Record<string, string>',
      },
    },
  ],
});
